import { supabaseClient, supabaseFunctionsUrl } from '@/services/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { isNonNullable, isNullable } from './nullability-helpers';
import type { TranslationKey } from '@/i18n';
import { FrontendError } from './error-helpers';
import type { FrontendScenarioDefinition } from '@shared-types/frontend-scenario-definition';
import type { PeriodKey, PublicScenarioDetails } from '@shared-types/scenario-definition';
import type { FrontendSimulationMetadata } from '@/types/simulation';
import type {
    CompetencyBIAssignmentInsert,
    Competency,
    CompetencyWithBIs,
    CompetencyResponseObject,
} from '@shared-types/competencies';
import type { EventRowInsert } from '@shared-types/events.types';
import type { CourseConfig, CourseData, CourseType } from '@shared-types/courses';
import type { CourseOutlineResponse } from '@shared-types/course/generate-course-response.types';
import type { DepartmentRoleAssignmentInsert } from '@shared-types/departments';
import type {
    GenerateCourseProperty,
    GenerateCoursePropertyPromptData,
} from '@shared-types/course/generate-course.types';
import type {
    AIConversationDecisionStartResponse,
    AIConversationDecisionResponse,
} from '@shared-types/course/ai-conversation-decision-response.types';
import type { OrganizationOtpRequestResult, OtpChannel } from '@shared-types/otp';

export class EdgeFunctionsManager {
    static getParticipantSignInData = (
        accessCode: string,
    ): Promise<{ email: string; otp: string; simulationID: string }> =>
        this.invokeEdgeFunction('get-participant-sign-in-data', { accessCode });

    static createSimulation = async (
        simulationMetadata: FrontendSimulationMetadata,
        organizationId: string,
    ): Promise<{ id: string }> => this.invokeEdgeFunction('simulations', { simulationMetadata, organizationId });

    static editSimulation = async (
        simulationID: string,
        simulationMetadata: FrontendSimulationMetadata,
    ): Promise<void> =>
        this.invokeEdgeFunction(`simulations/${encodeURIComponent(simulationID)}`, { simulationMetadata });

    static calculateNextPeriod = (simulationID: string, currentPeriodKey: PeriodKey): Promise<void> =>
        this.invokeEdgeFunction(`simulations/${encodeURIComponent(simulationID)}/calculate-next-period`, {
            currentPeriodKey,
        });

    static startPeriod = (simulationID: string): Promise<void> =>
        this.invokeEdgeFunction(`simulations/${encodeURIComponent(simulationID)}/start-period`, {});

    static sendParticipantInviteEmails = async (simulationID: string, sendToAll: boolean) =>
        this.invokeEdgeFunction('send-invite-emails', { simulationID, sendToAll });

    static sendPasswordResetEmail = (email: string, organizationId?: string): Promise<void> =>
        this.invokeEdgeFunction('send-reset-password-email', { email, organizationId });

    static changeEmail = (email: string, newEmail: string, organizationId?: string): Promise<void> =>
        this.invokeEdgeFunction('send-change-email-email', { email, newEmail, organizationId });

    static getPossibleScenarios = (): Promise<PublicScenarioDetails[]> =>
        this.invokeEdgeFunction('scenarios', undefined, 'GET');

    static getScenarioDefinition = (
        body: { simulationID: string; language?: string } | { scenarioID: string; language?: string },
    ): Promise<FrontendScenarioDefinition> => {
        if ('scenarioID' in body) {
            const params = new URLSearchParams();
            if (body.language) {
                params.set('language', body.language);
            }
            const query = params.toString();
            return this.invokeEdgeFunction(
                `scenarios/${encodeURIComponent(body.scenarioID)}${query ? `?${query}` : ''}`,
                undefined,
                'GET',
            );
        }

        const params = new URLSearchParams({ simulationId: body.simulationID });
        if (body.language) {
            params.set('language', body.language);
        }
        return this.invokeEdgeFunction(`scenarios?${params.toString()}`, undefined, 'GET');
    };

    // for getting response for ai-conversation activity
    static generateAIConversation = (
        elementId: string,
        scenarioKey: string,
        message?: string,
        previousResponseId?: string,
    ): Promise<AIConversationDecisionStartResponse | AIConversationDecisionResponse> =>
        this.invokeEdgeFunction('generate-ai-conversation', {
            elementId,
            scenarioKey,
            message,
            previousResponseId,
        });

    static generateAIContent = (body: {
        promptData: Record<string, string | undefined>;
        promptFilePaths: string[];
        sourceDocuments: string[];
        competencyIds?: string[]; // Add competency IDs for enhanced chunk retrieval
        json_schema: string;
        organizationId: string;
        params?: Record<string, any>;
    }): Promise<any> => this.invokeEdgeFunction('generate-ai-content', body);

    static transcribeAudio = (formData: FormData): Promise<any> =>
        this.invokeEdgeFunction('transcribe-audio', formData);

    static generateAudio = (formData: FormData): Promise<any> => this.invokeEdgeFunction('text-to-speech', formData);

    // Image related AI functions
    static generateImage = (prompt: string, args?: any): Promise<any> =>
        this.invokeEdgeFunction('generate-image', { prompt, args });

    // --------------------------------------------------------------
    // competencies related functions
    // --------------------------------------------------------------
    static getCompetencies = (body: {
        organizationId: string;
        includeBICount?: boolean;
        searchValue?: string;
        page?: number;
        limit?: number;
        roleIds?: string[];
    }): Promise<CompetencyResponseObject> => {
        const params = new URLSearchParams({ organizationId: body.organizationId });
        if (body.includeBICount !== undefined) {
            params.set('includeBICount', String(body.includeBICount));
        }
        if (body.searchValue) {
            params.set('searchValue', body.searchValue);
        }
        if (body.page !== undefined) {
            params.set('page', String(body.page));
        }
        if (body.limit !== undefined) {
            params.set('limit', String(body.limit));
        }
        for (const roleId of body.roleIds ?? []) {
            params.append('roleIds', roleId);
        }

        return this.invokeEdgeFunction(`competencies?${params.toString()}`, undefined, 'GET');
    };

    static getCompetency = (body: {
        competencyId: string;
        organizationId: string;
        version?: number | string;
        includeBIs?: boolean;
    }): Promise<Competency | CompetencyWithBIs> => {
        const params = new URLSearchParams({ organizationId: body.organizationId });
        if (body.version !== undefined) {
            params.set('version', String(body.version));
        }
        if (body.includeBIs !== undefined) {
            params.set('includeBIs', String(body.includeBIs));
        }

        return this.invokeEdgeFunction(
            `competencies/${encodeURIComponent(body.competencyId)}?${params.toString()}`,
            undefined,
            'GET',
        );
    };

    static verifyJwt = (jwt: string): Promise<{ token: string; email: string; simulationId: string }> =>
        this.invokeEdgeFunction('verify-jwt', { jwt });

    // --------------------------------------------------------------
    // course / activity generation related functions
    // --------------------------------------------------------------

    // always start with generating learning objectives
    static courseGenerateStart = (body: {
        promptData: Record<string, unknown>;
        sourceDocuments: string[];
        organizationId: string;
    }): Promise<any> => this.invokeEdgeFunction('generate-course-content/learning-objectives', body);

    static courseGenerateOutline = (body: {
        promptData: Record<string, string | undefined>;
        conversationId: string;
        organizationId: string;
        subModules?: boolean;
    }): Promise<any> => this.invokeEdgeFunction('generate-course-content/outline', body);

    static courseGenerateProperty = <T extends GenerateCourseProperty>(body: {
        propertyName: T;
        promptData: GenerateCoursePropertyPromptData<T>;
        previousResponseId: string;
        organizationId: string;
    }): Promise<any> => this.invokeEdgeFunction('generate-course-content/property', body);

    static courseGenerateActivities = (body: {
        courseOutline: CourseOutlineResponse;
        previousResponseId: string;
        documentIds: string[];
        competencies: CompetencyBIAssignmentInsert[];
        departmentRoles: DepartmentRoleAssignmentInsert[];
        organizationId: string;
        learningObjectives: string[];
        courseType?: CourseType;
        createScenarios?: boolean;
        scenarioCount?: string;
    }): Promise<any> => this.invokeEdgeFunction('generate-course-content/activity', body);

    // --------------------------------------------------------------
    // course related functions
    // --------------------------------------------------------------
    static getUserAssignedCourses = (
        organizationId: string,
        language?: string,
        role?: 'admin' | 'participant',
    ): Promise<CourseData[]> => {
        const params = new URLSearchParams({ organizationId });
        if (language) {
            params.set('language', language);
        }
        if (role) {
            params.set('role', role);
        }
        return this.invokeEdgeFunction(`courses?${params.toString()}`, undefined, 'GET');
    };

    static getCourseDetails = (
        courseId: string,
        userId: string,
        organizationId: string,
        children: boolean = false,
        language?: string,
        role?: 'admin' | 'user',
    ): Promise<CourseData> => {
        const params = new URLSearchParams({ organizationId, userId });
        if (children) {
            params.set('children', 'true');
        }
        if (language) {
            params.set('language', language);
        }
        if (role) {
            params.set('role', role);
        }
        return this.invokeEdgeFunction(
            `courses/${encodeURIComponent(courseId)}?${params.toString()}`,
            undefined,
            'GET',
        );
    };

    static startCourse = (
        courseId: string,
        userId: string,
        organizationId: string,
        config: CourseConfig,
    ): Promise<void> =>
        this.invokeEdgeFunction(`courses/${encodeURIComponent(courseId)}/start`, { userId, organizationId, config });

    /**
     * Start a course-embedded simulation. Calls POST /api/course-attempts/:courseAttemptId/simulation/:courseId/start.
     * Returns { simulationId } for navigation to the simulation.
     */
    static startCourseSimulation = async (
        courseAttemptId: string,
        courseId: string,
        userId: string,
        organizationId: string,
    ): Promise<{ simulationId: string }> => {
        const {
            data: { session },
        } = await supabaseClient.auth.getSession();
        const token = session?.access_token;
        if (!token) {
            throw new Error('Not authenticated');
        }
        const url = `${supabaseFunctionsUrl}/api/course-attempts/${courseAttemptId}/simulation/${courseId}/start`;
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userId, organizationId }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.message ?? `Failed to start simulation: ${res.status}`);
        }
        return res.json();
    };

    // --------------------------------------------------------------
    // task related functions
    // --------------------------------------------------------------
    static startTask = (courseId: string, userId: string, organizationId: string): Promise<void> =>
        this.invokeEdgeFunction('courses/task/start', { courseId, userId, organizationId });

    static submitTaskDecisions = (
        taskAttemptId: string,
        userId: string,
        decisions: Record<string, any>,
        taskFinished?: boolean,
    ): Promise<void> =>
        this.invokeEdgeFunction(`courses/task/${encodeURIComponent(taskAttemptId)}/decisions`, {
            userId,
            decisions,
            taskFinished,
        });

    // Save-without-finishing, used for periodic autosave. Unlike submitTaskDecisions/invokeEdgeFunction,
    // this rethrows the raw error (a FunctionsHttpError for HTTP errors, carrying the status on
    // error.context.status) instead of collapsing it into a FrontendError, since the autosave
    // composable needs to tell "attempt no longer started" (409) apart from a network failure
    // (which should retry) by inspecting the error itself in its own try/catch.
    //
    // `keepalive: true` is for the one case where the caller is about to unload the page, it has to be a
    // raw fetch instead. Since there's no time to await a fresh session in that moment, the caller
    // passes in an access token it already cached ahead of time.
    static autosaveTaskDecisions = async (
        taskAttemptId: string,
        userId: string,
        decisions: Record<string, any>,
        options?: { keepalive: true; accessToken: string | undefined },
    ): Promise<void> => {
        const path = `courses/task/${encodeURIComponent(taskAttemptId)}/decisions`;

        if (options?.keepalive) {
            if (isNullable(options.accessToken)) {
                return;
            }
            await fetch(`${supabaseFunctionsUrl}/${path}`, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${options.accessToken}`,
                },
                body: JSON.stringify({ userId, decisions, taskFinished: false }),
            }).catch(() => {});
            return;
        }

        const { error } = await supabaseClient.functions.invoke(path, {
            body: { userId, decisions, taskFinished: false },
        });

        if (isNonNullable(error)) {
            throw error;
        }
    };

    static evaluateTask = (taskAttemptId: string): Promise<void> =>
        this.invokeEdgeFunction(`courses/task/${encodeURIComponent(taskAttemptId)}/evaluate`, {});

    static addEvent = (event: EventRowInsert): Promise<void> => this.invokeEdgeFunction('add-event', { event });

    // --------------------------------------------------------------
    // organization phone-auth related functions
    // --------------------------------------------------------------
    static requestOrganizationOtp = (
        organizationId: string,
        phone: string,
        channel: OtpChannel,
    ): Promise<OrganizationOtpRequestResult> =>
        this.invokeEdgeFunction('otp/organization', { organizationId, phone, channel });

    static getOtpChannelConfig = (): Promise<{ smsEnabled: boolean; whatsappEnabled: boolean }> =>
        this.invokeEdgeFunction('otp/channel-config', undefined, 'GET');

    static requestPhoneLoginOtp = (phone: string, channel: OtpChannel): Promise<void> =>
        this.invokeEdgeFunction('otp/phone-login', { phone, channel });

    static completeOrganizationOtp = (organizationId: string): Promise<{ role?: string; organizationId: string }> =>
        this.invokeEdgeFunction('complete-organization-otp', { organizationId });

    static invokeEdgeFunction = async (
        name: string,
        body?: Record<string, any>,
        method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    ) => {
        const { data, error } = await supabaseClient.functions.invoke(name, { body, method });
        if (isNonNullable(error)) {
            if (error instanceof FunctionsHttpError) {
                const { error: errorData } = (await error.context.json()) as { error: { code: string } };

                // since we are using type case before, there's no guarantee about the error and code
                // thus we need to add null checks and fallback to a default code
                const code = errorData?.code ?? 'couldNotProcess';
                throw new FrontendError(`errors.edgeFunctions.${code}` as TranslationKey);
            } else {
                throw error;
            }
        }
        return data;
    };
}
